// The score: an original laid-back jazz loop in F major (D-02), plus the pure Transport that times it.
// The lead instrument, tempo and band follow the time of day, and mood changes land on the next bar line
// so the song never stops mid-phrase. Port of look-demo audio.ts.

export type Note = [beat: number, len: number, midi: number];
export interface Chord { at: number; len: number; voicing: number[]; root: number }
export type Lead = 'glock' | 'marimba' | 'flute' | 'musicbox';
export interface Mood { bpm: number; lead: Lead; shift: number; ep: number; bass: number; shaker: number; rim: number; kick: number }

export const hz = (m: number): number => 440 * Math.pow(2, (m - 69) / 12);

// Fmaj7 | Em7 A7 | Dm7 | Cm7 F7 | Bbmaj7 | Am7 D7 | Gm7 | C7sus4 C7
export const CHORDS: readonly Chord[] = [
  { at: 0, len: 4, voicing: [53, 57, 60, 64], root: 41 },
  { at: 4, len: 2, voicing: [52, 55, 59, 62], root: 40 },
  { at: 6, len: 2, voicing: [52, 55, 57, 61], root: 45 },
  { at: 8, len: 4, voicing: [53, 57, 60, 62], root: 38 },
  { at: 12, len: 2, voicing: [51, 55, 58, 60], root: 36 },
  { at: 14, len: 2, voicing: [51, 57, 60, 65], root: 41 },
  { at: 16, len: 4, voicing: [50, 53, 57, 62], root: 46 },
  { at: 20, len: 2, voicing: [52, 55, 57, 60], root: 45 },
  { at: 22, len: 2, voicing: [54, 57, 60, 62], root: 38 },
  { at: 24, len: 4, voicing: [53, 55, 58, 62], root: 43 },
  { at: 28, len: 2, voicing: [53, 55, 58, 60], root: 36 },
  { at: 30, len: 2, voicing: [52, 55, 58, 60], root: 36 },
];
// The tune, then its answer. Every long note sits on a chord tone or a ninth.
export const TUNE: readonly Note[] = [
  [1, 0.5, 72], [1.5, 0.5, 69], [2, 1, 72], [3, 1, 76],
  [4, 1.5, 74], [5.5, 0.5, 72], [6, 1, 73], [7, 1, 76],
  [8, 1.5, 77], [9.5, 0.5, 76], [10, 1, 74], [11, 1, 69],
  [12, 1, 70], [13, 0.5, 67], [13.5, 0.5, 70], [14, 1, 69], [15, 1, 75],
  [16, 1, 74], [17, 1, 77], [18, 0.5, 69], [18.5, 0.5, 70], [19, 1, 72],
  [20, 1.5, 76], [21.5, 0.5, 72], [22, 0.5, 72], [22.5, 0.5, 74], [23, 1, 78],
  [24, 1, 77], [25, 1, 74], [26, 1, 70], [27, 1, 67],
  [28, 1.5, 67], [29.5, 0.5, 70], [30, 1.5, 76],
];
export const ANSWER: readonly Note[] = [
  [0, 1, 77], [1, 0.5, 76], [1.5, 0.5, 72], [2, 2, 69],
  [4.5, 0.5, 67], [5, 0.5, 71], [5.5, 0.5, 74], [6, 1.5, 73], [7.5, 0.5, 69],
  [8, 2, 74], [10, 0.5, 77], [10.5, 0.5, 76], [11, 1, 74],
  [12, 1, 72], [13, 1, 70], [14, 0.5, 69], [14.5, 0.5, 72], [15, 1, 75],
  [16, 3, 74], [19, 1, 72],
  [20, 1, 72], [21, 1, 69], [22, 1, 78], [23, 0.5, 76], [23.5, 0.5, 74],
  [24, 1.5, 74], [25.5, 0.5, 70], [26, 1, 69], [27, 1, 67],
  [28, 1, 65], [29, 1, 67], [30, 1, 70], [31, 1, 72],
];
// Comping and bass figures for a whole-bar chord and a half-bar chord: [offset in beats, length, velocity or interval].
export const EP_FULL: readonly (readonly [number, number, number])[] = [[0, 1.3, 0.9], [1.5, 0.8, 0.6], [3, 0.9, 0.7]];
export const EP_HALF: readonly (readonly [number, number, number])[] = [[0, 1.2, 0.85], [1.5, 0.45, 0.55]];
export const BASS_FULL: readonly (readonly [number, number, number])[] = [[0, 1.4, 0], [1.5, 0.45, 7], [3, 0.9, 0]];
export const BASS_HALF: readonly (readonly [number, number, number])[] = [[0, 1.3, 0], [1.5, 0.4, 7]];

/** 0 morning, 1 midday, 2 golden, 3 dusk. */
export const MOODS: readonly Mood[] = [
  { bpm: 94, lead: 'glock', shift: 12, ep: 0.75, bass: 0.8, shaker: 0.55, rim: 0, kick: 0 },
  { bpm: 102, lead: 'marimba', shift: 0, ep: 0.85, bass: 1, shaker: 0.8, rim: 0.7, kick: 0.6 },
  { bpm: 86, lead: 'flute', shift: 0, ep: 0.9, bass: 0.9, shaker: 0.45, rim: 0, kick: 0.45 },
  { bpm: 74, lead: 'musicbox', shift: 12, ep: 0.7, bass: 0.6, shaker: 0, rim: 0, kick: 0 },
];

/** A song (title screen, 2026-10-05: the town loop and the title theme share the band): its chords over one `length`-beat section, and
 *  what the lead plays in each section in turn, round and round. A null section is the band's fills on chord tones; `leads` gives a
 *  section its own instrument (else the mood's). */
export interface Score { chords: readonly Chord[]; sections: readonly (readonly Note[] | null)[]; leads?: readonly (Lead | null)[]; length: number }
/** The town loop: the tune, its answer, then a section of fills. */
export const TOWN_SCORE: Score = { chords: CHORDS, sections: [TUNE, ANSWER, null], length: 32 };
/** The section playing at `beat` (in beats from the song's start): its notes (null = fills) and its lead instrument. */
export function sectionAt(score: Score, beat: number, mood: Mood): { notes: readonly Note[] | null; lead: Lead } {
  const i = Math.floor(beat / score.length) % score.sections.length;
  return { notes: score.sections[i] ?? null, lead: score.leads?.[i] ?? mood.lead };
}

/** The song's clock: eighth-note steps scheduled a little ahead of the audio clock. Pure, so it is testable without Web Audio.
 *  `moods` is the song's own set (the town's four by default; the title theme has one). */
export class Transport {
  beat = 0; nextTime: number; mood: number; pending: number;
  private readonly moods: readonly Mood[];
  constructor(start: number, mood: number, moods: readonly Mood[] = MOODS) { this.nextTime = start; this.mood = mood; this.pending = mood; this.moods = moods; }
  setMood(i: number): void { this.pending = i; }
  /** Calls `step` for every eighth note due before now + ahead; a mood change lands on the next bar line. */
  advance(now: number, ahead: number, step: (beat: number, time: number, mood: Mood) => void): void {
    // If the page stalled (a long first render, a hidden tab), skip the missed steps instead of playing them all at once.
    const late = now - this.nextTime;
    if (late > 0.05) {
      const len = 0.5 * (60 / this.moods[this.mood]!.bpm), n = Math.ceil(late / len);
      this.beat += n * 0.5; this.nextTime += n * len;
    }
    while (this.nextTime < now + ahead) {
      if ((this.beat % 32) % 4 === 0) this.mood = this.pending;
      step(this.beat, this.nextTime, this.moods[this.mood]!);
      this.nextTime += 0.5 * (60 / this.moods[this.mood]!.bpm);
      this.beat += 0.5;
    }
  }
}
